import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { appeler, renouvelerSession } from '../../api/client.ts';
import { Carte, Erreur } from '../../composants/communs.tsx';

/**
 * Création d'une boutique — RG-012, RG-013.
 *
 * L'écran Boutiques listait, paramétrait et désactivait, mais n'offrait
 * AUCUN moyen de créer : `POST /boutiques` existait côté serveur depuis le
 * début et n'était relié à rien. Ouvrir une deuxième boutique — le cœur du
 * produit — était donc impossible depuis l'administration.
 *
 * Le formulaire insiste sur une seule chose : le CODE est définitif. Il entre
 * dans la numérotation de tous les documents de la boutique
 * (`COT1-VTE-2026-000042`), et un trigger en base en interdit la modification.
 * Le dire après coup ne servirait à rien — on le montre en exemple vivant
 * pendant la saisie.
 */
export function FormulaireBoutique({ onFerme }: { onFerme: () => void }): ReactNode {
  const cache = useQueryClient();

  const [code, setCode] = useState('');
  const [nom, setNom] = useState('');
  const [adresse, setAdresse] = useState('');
  const [telephone, setTelephone] = useState('');
  const [estDepot, setEstDepot] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      appeler('/boutiques', {
        methode: 'POST',
        corps: {
          id: crypto.randomUUID(),
          code,
          nom: nom.trim(),
          adresse: adresse.trim() === '' ? null : adresse.trim(),
          telephone: telephone.trim() === '' ? null : telephone.trim(),
          est_depot: estDepot,
        },
      }),
    onSuccess: async () => {
      /*
        Le jeton est renouvelé AVANT de relire les listes.

        La portée — la liste des boutiques qu'un compte peut voir — est gravée
        dans le jeton d'accès et figée pour sa durée de vie. Le serveur vient
        de rattacher la boutique à son créateur, mais le jeton en circulation
        l'ignore : sans ce renouvellement, la création répond 201 et la
        boutique n'apparaît NULLE PART, ni dans la liste, ni dans le sélecteur.
        C'était précisément le symptôme « impossible de créer une boutique ».
      */
      await renouvelerSession();

      await cache.invalidateQueries({ queryKey: ['boutiques-admin'] });
      // Le sélecteur de contexte vit sur une AUTRE requête : sans cette
      // seconde invalidation, la boutique existe mais reste absente du menu.
      await cache.invalidateQueries({ queryKey: ['boutiques'] });
      onFerme();
    },
  });

  /**
   * Le code est mis en majuscules À LA SAISIE.
   *
   * Le serveur le fait de toute façon (`trim().toUpperCase()`). Le montrer
   * tout de suite évite de créer « cot2 » et de lire « COT2 » ensuite, en se
   * demandant lequel des deux a été enregistré.
   */
  function saisirCode(valeur: string): void {
    setCode(valeur.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8));
  }

  const codeValide = /^[A-Z0-9]{3,8}$/.test(code);
  const exercice = new Date().getFullYear();

  return (
    <Carte>
      <form
        onSubmit={(evenement: SyntheticEvent) => {
          evenement.preventDefault();
          if (codeValide && nom.trim() !== '') {
            mutation.mutate();
          }
        }}
      >
        <h2>Nouvelle boutique</h2>
        <p className="entete__sous" style={{ marginBottom: 14 }}>
          Le code entre dans la numérotation de tous ses documents et ne pourra
          plus être changé.
        </p>

        <div className="champs">
          <div className="champ">
            <label htmlFor="code-boutique">Code</label>
            <input
              id="code-boutique"
              autoFocus
              value={code}
              placeholder="COT2"
              aria-describedby="apercu-numero"
              onChange={(evenement) => {
                saisirCode(evenement.target.value);
              }}
            />
            {/*
              L'exemple vivant vaut mieux qu'un avertissement : on voit ce que
              le code produira sur chaque ticket, avant de valider.
            */}
            <div id="apercu-numero" className="entete__sous">
              {codeValide ? (
                <>
                  Vos ventes porteront{' '}
                  <span className="mono">
                    {code}-VTE-{exercice}-000001
                  </span>
                </>
              ) : (
                '3 à 8 lettres ou chiffres.'
              )}
            </div>
          </div>

          <div className="champ">
            <label htmlFor="nom-nouvelle-boutique">Nom</label>
            <input
              id="nom-nouvelle-boutique"
              value={nom}
              placeholder="Cotonou Dantokpa"
              onChange={(evenement) => {
                setNom(evenement.target.value);
              }}
            />
          </div>

          <div className="champ">
            <label htmlFor="adresse-boutique">Adresse</label>
            <input
              id="adresse-boutique"
              value={adresse}
              onChange={(evenement) => {
                setAdresse(evenement.target.value);
              }}
            />
          </div>

          <div className="champ">
            <label htmlFor="telephone-boutique">Téléphone</label>
            <input
              id="telephone-boutique"
              value={telephone}
              placeholder="+229…"
              onChange={(evenement) => {
                setTelephone(evenement.target.value);
              }}
            />
          </div>
        </div>

        <label style={{ display: 'block', marginTop: 4 }}>
          <input
            type="checkbox"
            style={{ width: 'auto', marginRight: 8 }}
            checked={estDepot}
            onChange={(evenement) => {
              setEstDepot(evenement.target.checked);
            }}
          />
          Dépôt central : approvisionne les boutiques, ne vend pas
        </label>

        <Erreur erreur={mutation.error} />

        <div className="ligne" style={{ marginTop: 14 }}>
          <button
            type="submit"
            disabled={!codeValide || nom.trim() === '' || mutation.isPending}
          >
            {mutation.isPending ? 'Création…' : 'Créer la boutique'}
          </button>
          <button type="button" className="bouton--discret" onClick={onFerme}>
            Annuler
          </button>
        </div>
      </form>
    </Carte>
  );
}
